import React, { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { motion } from "framer-motion";

const style = {
  mainDiv: "w-full flex flex-col",
  labelText:
    "w-full text-[#D9D9D9]/70 text-[14px] font-light font-poppins z-10",
  inputDiv:
    "w-full flex flex-row items-center justify-between border border-[#ffffff]/10 mt-2 px-3 py-3 rounded-[8px] bg-[#1a1a1a]",
  inputBox:
    "bg-transparent text-[#ffffff]/80 font-poppins w-full placeholder:text-gray-500",
  dropDownSelection: "text-sm cursor-pointer font-normal bg-[#1a1a1a]",
  selectDiv:
    "flex items-center px-2 py-2 text-[14px] rounded-md text-neutral-300 hover:bg-[#FFFFFF1A]/10 hover:text-white/85 ease-linear duration-150 cursor-pointer",
  priceDiv:
    "flex flex-col items-center border border-[#ffffff]/10 px-2 py-1 rounded-[8px] bg-[#1a1a1a]",
};

const CandleChart = ({ symbol, interval, duration, handleSubmit }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [noData, setNoData] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [customBuyValue, setCustomBuyValue] = useState("");
  const [customSellValue, setCustomSellValue] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  const [buyDate, setBuyDate] = useState(null); 
  const [sellDate, setSellDate] = useState(null); 
  const [clickPhase, setClickPhase] = useState("first"); // "first", "second", or "complete"
  const [statusMessage, setStatusMessage] = useState("");
  
  function convertToUTC(unixTime) {
    const date = new Date(unixTime * 1000);
  
    const dd = String(date.getUTCDate()).padStart(2, '0');
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = date.getUTCFullYear();
    const HH = String(date.getUTCHours()).padStart(2, '0');
    const MM = String(date.getUTCMinutes()).padStart(2, '0');
    const SS = String(date.getUTCSeconds()).padStart(2, '0');
  
    return `${dd}-${mm}-${yyyy} ${HH}:${MM}:${SS}`;
  }
  
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const initializeChart = () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
      }

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 450,
        layout: {
          background: { type: "solid", color: "black" },
          textColor: "white",
        },
        grid: { vertLines: { color: "#444" }, horzLines: { color: "#444" } },
        timeScale: { timeVisible: true, borderColor: "#71649C" },
        rightPriceScale: { borderColor: "#71649C" },
      });

      chartRef.current = chart;
      const series = chart.addCandlestickSeries();
      series.applyOptions({
        upColor: "#26a69a",
        downColor: "#ef5350",
        wickUpColor: "#26a69a",
        wickDownColor: "#ef5350",
        borderVisible: false,
      });
      seriesRef.current = series;

      return chart;
    };

    let chart;
    try {
      chart = initializeChart();
    } catch (e) {
      console.error("Failed to initialize chart:", e);
      return;
    }

    const fetchData = async () => {
      try {
        const endTime = Date.now();
        const days = parseInt(duration);
        const startTime = endTime - days * 24 * 60 * 60 * 1000;

        const response = await fetch(
          `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${interval}&startTime=${startTime}&endTime=${endTime}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data || data.length === 0) {
          setNoData(true);
          if (seriesRef.current) {
            seriesRef.current.setData([]);
          }
          return;
        }

        setNoData(false);
        const formattedData = data.map((d) => ({
          time: d[0] / 1000,
          open: parseFloat(d[1]),
          high: parseFloat(d[2]),
          low: parseFloat(d[3]),
          close: parseFloat(d[4]),
        }));

        if (seriesRef.current) {
          seriesRef.current.setData(formattedData);
        }
        chart.timeScale().fitContent();

        // Handle chart click events - new simplified logic
        chart.subscribeClick((param) => {
          if (!param.point || !param.time) return;
          
          // Find the candle data at the clicked time
          const candleData = formattedData.find((d) => d.time === param.time);
          if (!candleData) return;
          
          const priceInfo = {
            time: convertToUTC(candleData.time),
            open: candleData.open,
            high: candleData.high,
            low: candleData.low,
            close: candleData.close,
          };
          
          setSelectedPrice(priceInfo);
          
          // If position isn't selected yet, prompt the user
          if (!selectedPosition) {
            setStatusMessage("Please select a position (LONG/SHORT) first");
            return;
          }
          
          // First click sets buy info
          if (clickPhase === "first") {
            setBuyDate(priceInfo.time);
            
            // For LONG positions, use the low price for buying
            // For SHORT positions, use the high price for selling first
            if (selectedPosition === "LONG") {
              setCustomBuyValue(priceInfo.high);
              setStatusMessage("Buy position set! Click another candle to set sell position.");
            } else { // SHORT
              setCustomSellValue(priceInfo.low);
              setStatusMessage("Short sell position set! Click another candle to set buy-back position.");
            }
            
            setClickPhase("second");
          } 
          // Second click sets sell info
          else if (clickPhase === "second") {
            setSellDate(priceInfo.time);
            
            // For LONG positions, use the high price for selling
            // For SHORT positions, use the low price for buying back
            if (selectedPosition === "LONG") {
              setCustomSellValue(priceInfo.low);
            } else { // SHORT
              setCustomBuyValue(priceInfo.high);
            }
            
            setClickPhase("complete");
            setStatusMessage("All positions set! You can now place your order.");
          }
        });

        chart.subscribeCrosshairMove((param) => {
          if (param.time && seriesRef.current && param.seriesData) {
            const data = param.seriesData.get(seriesRef.current);
            if (data) {
              setSelectedPrice({
                time: convertToUTC(param.time),
                open: data.open,
                high: data.high,
                low: data.low,
                close: data.close,
              });
            }
          }
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setNoData(true);
        if (seriesRef.current) {
          seriesRef.current.setData([]);
        }
      }
    };

    fetchData();

    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
      }
    };
  }, [symbol, interval, duration, selectedPosition, clickPhase]);

  // Reset the buy/sell date state when position changes
  useEffect(() => {
    setBuyDate(null);
    setSellDate(null);
    setCustomBuyValue("");
    setCustomSellValue("");
    setClickPhase("first");
    setStatusMessage(selectedPosition ? "Click on a candle to set your first position" : "");
  }, [selectedPosition]);

  // Reset button to start over
  const handleReset = () => {
    setBuyDate(null);
    setSellDate(null);
    setCustomBuyValue("");
    setCustomSellValue("");
    setClickPhase("first");
    setStatusMessage(selectedPosition ? "Click on a candle to set your first position" : "");
  };

  // Check if button should be enabled
  const isButtonEnabled = () => {
    const enabled = (
      !!selectedPosition &&
      quantity !== "" &&
      quantity >= 1 &&
      customBuyValue !== "" &&
      parseFloat(customBuyValue) > 0 &&
      customSellValue !== "" &&
      parseFloat(customSellValue) > 0 &&
      buyDate !== null &&
      sellDate !== null
    );
    return enabled;
  };

  return (
    <div className="w-full h-fit grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div
        className="w-full md:col-span-2 h-fit pt-[20px] xl:pt-[100px]"
        ref={chartContainerRef}
        style={{ width: "100%", height: "450px" }}
      />
      {noData && (
        <div className="text-white text-center mt-4">No Data Found</div>
      )}

      
      
      <div className="md:col-span-1 bg-[#000000] rounded-lg px-4">
         {/* Status message area */}
         {statusMessage && (
          <div className="mt-3 w-full py-3 px-4 mb-4 bg-blue-900/40 rounded-md">
            <div className="text-blue-300 text-sm font-medium text-center">
              {statusMessage}
            </div>
          </div>
        )}
        
        {selectedPrice && !noData && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className={style.priceDiv}>
              <span className="text-gray-400 uppercase font-medium text-xs">
                High Price
              </span>
              <span className="font-mono text-green-400 mt-1 ">
                {selectedPrice.high.toFixed(5)}
              </span>
            </div>
            <div className={style.priceDiv}>
              <span className="text-gray-400 uppercase font-medium text-xs">
                Low Price
              </span>
              <span className="font-mono text-red-400 mt-1">
                {selectedPrice.low.toFixed(5)}
              </span>
            </div>
          </div>
        )}
        
        <div className={`${style.mainDiv}  text-white`}>
          <div className={style.labelText}>Position</div>
          <div className="relative">
            <div
              onClick={() => setShowPositionDropdown(!showPositionDropdown)}
              className={`${style.inputDiv} flex justify-between cursor-pointer`}
            >
              <div className={style.dropDownSelection}>
                {selectedPosition || "Select Position"}
              </div>
              <div className="pl-2 ease-linear duration-150 text-white">
                {showPositionDropdown ? (
                  <ChevronUpIcon className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-white/50" />
                )}
              </div>
            </div>
            {showPositionDropdown && (
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, type: "spring" }}
                className="absolute z-20 w-full bg-[#1a1a1a] rounded-md mt-1"
              >
                <div
                  className={style.selectDiv}
                  onClick={() => {
                    setSelectedPosition("LONG");
                    setShowPositionDropdown(false);
                  }}
                >
                  LONG
                </div>
                <div
                  className={style.selectDiv}
                  onClick={() => {
                    setSelectedPosition("SHORT");
                    setShowPositionDropdown(false);
                  }}
                >
                  SHORT
                </div>
              </motion.div>
            )}
          </div>
        </div>
        <div className={`${style.mainDiv} mt-2`}>
          <div className={style.labelText}>Quantity</div>
          <div className={style.inputDiv}>
            <input
              type="number"
              value={quantity}
              placeholder="Enter quantity (e.g., 1, 10, 50)"
              className={style.inputBox}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || parseInt(value) < 1) {
                  setQuantity("");
                } else {
                  setQuantity(parseInt(value));
                }
              }}
            />
          </div>
        </div>
        <div className={`${style.mainDiv} mt-2`}>
          {selectedPosition === "LONG" && (
            <>
              <div className={`${style.mainDiv} mt-2`}>
                <div className="flex justify-between items-center">
                  <div className={style.labelText}>
                    Buy Value
                    {buyDate && (
                      <span className="ml-[20px] text-xs text-blue-400">
                        {buyDate}
                      </span>
                    )}
                  </div>
                </div>
                <div className={style.inputDiv}>
                  <input
                    type="number"
                    value={customBuyValue}
                    placeholder="Click on a candle to set buy position"
                    className={style.inputBox}
                    onChange={(e) => setCustomBuyValue(e.target.value)}
                  />
                </div>
              </div>
              <div className={`${style.mainDiv} mt-2`}>
                <div className="flex justify-between items-center">
                  <div className={style.labelText}>
                    Sell Value
                    {sellDate && (
                      <span className="ml-[20px] text-xs text-blue-400">
                        {sellDate}
                      </span>
                    )}
                  </div>
                </div>
                <div className={style.inputDiv}>
                  <input
                    type="number"
                    value={customSellValue}
                    placeholder="Click on a second candle to set sell position"
                    className={style.inputBox}
                    onChange={(e) => setCustomSellValue(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
          {selectedPosition === "SHORT" && (
            <>
              <div className={`${style.mainDiv} mt-2`}>
                <div className="flex justify-between items-center">
                  <div className={style.labelText}>
                    Sell Value
                    {sellDate && (
                      <span className="ml-[20px] text-xs text-blue-400">
                        {buyDate}
                      </span>
                    )}
                  </div>
                </div>
                <div className={style.inputDiv}>
                  <input
                    type="number"
                    value={customSellValue}
                    placeholder="Click on a candle to set sell position"
                    className={style.inputBox}
                    onChange={(e) => setCustomSellValue(e.target.value)}
                  />
                </div>
              </div>
              <div className={`${style.mainDiv} mt-2`}>
                <div className="flex justify-between items-center">
                  <div className={style.labelText}>
                    Buy Value
                    {buyDate && (
                      <span className="ml-[20px] text-xs text-blue-400">
                        {sellDate}
                      </span>
                    )}
                  </div>
                </div>
                <div className={style.inputDiv}>
                  <input
                    type="number"
                    value={customBuyValue}
                    placeholder="Click on a second candle to set buy-back position"
                    className={style.inputBox}
                    onChange={(e) => setCustomBuyValue(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </div>

       
        
        <div className="mt-4 flex gap-2">
          {clickPhase !== "first" && (
            <button
              className="flex-1 text-white rounded-md bg-gray-700 px-6 py-2 font-bold hover:bg-gray-600 transition-colors"
              onClick={handleReset}
            >
              Reset
            </button>
          )}
          <button
            className="flex-1 text-white rounded-md bg-[#409538] px-6 py-2 font-bold hover:bg-[#357e2f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              if (!selectedPosition) {
                alert("Please select a position");
                return;
              }
              if (!customBuyValue || parseFloat(customBuyValue) <= 0) {
                alert("Please enter a valid buy value");
                return;
              }
              if (!customSellValue || parseFloat(customSellValue) <= 0) {
                alert("Please enter a valid sell value");
                return;
              }
              if (!buyDate) {
                alert("Please select a candle for the buy date");
                return;
              }
              if (!sellDate) {
                alert("Please select a candle for the sell date");
                return;
              }
              const currentDate = new Date();
              const formattedDate = `${String(currentDate.getDate()).padStart(2, '0')}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${currentDate.getFullYear()} ${String(currentDate.getHours()).padStart(2, '0')}:${String(currentDate.getMinutes()).padStart(2, '0')}:${String(currentDate.getSeconds()).padStart(2, '0')}`;

              console.log("Submitting order with data:", {
                quantity,
                buy_value: customBuyValue,
                sell_value: customSellValue,
                position: selectedPosition,
                buy_date: buyDate,
                sell_date: sellDate,
                current_date: formattedDate,
              });
             
              if (handleSubmit) {
                handleSubmit({
                  quantity,
                  buy_value: customBuyValue,
                  sell_value: customSellValue,
                  position: selectedPosition,
                  buy_date: buyDate,
                  sell_date: sellDate,
                  current_date: formattedDate,
                });
              }
            }}
            disabled={!isButtonEnabled()}
          >
            Place Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandleChart;

